import { Center, CircularProgress, Flex, Text, useColorMode } from '@chakra-ui/react'
import { DataGridContext, DataTableProps, SearchBarContext, useApp, useAuth } from '@hb/react'
import {
  AnyObject,
  getReverseName,
  isListDividerItem,
  ListItem,
  makePlural,
  WithId,
} from '@hb/shared'
import { DocumentData } from 'firebase/firestore'
import { forwardRef, JSX, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { GridChildComponentProps, VariableSizeGrid } from 'react-window'
import { usePopulatedItem } from '../../hooks/backend/usePopulatedItem'
import { UserCellComponentProps } from '../../types'
import { getRowBackground, getRowDarkBackground } from '../DataView/utils'
import { ListDividerView } from './dividers/ListDividerView'
import { usePreview } from './hooks'
import { DesktopRowHeaders } from './RowHeaders'
import { getFlexColumnWidth, getItemsWithDividers } from './utils'

const UserCell = <
  Snippet extends DocumentData,
  ExtraColumnProps extends AnyObject | undefined = AnyObject | undefined,
>({
  columnIndex,
  rowIndex,
  style,
  isScrolling,
  data,
}: GridChildComponentProps<UserCellComponentProps<Snippet, ExtraColumnProps>>) => {
  const {
    items,
    columns,
    preview,
    auth,
    app,
    tabName,
    tab,
    onRowClick,
    listWidth,
    extraColumnProps,
    searchQuery,
    admins,
  } = data
  const { Render, flexProps, width } = columns[Object.keys(columns)[columnIndex]]
  const item = items[rowIndex]
  if (isListDividerItem(item)) {
    if (columnIndex === 0) {
      return (
        <div style={{ ...style, width: `${listWidth}px` }}>
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <ListDividerView item={item} />
          </div>
        </div>
      )
    }
    return null
  }
  const background = tab.getItemBackgroundColor
    ? tab.getItemBackgroundColor(item, rowIndex)
    : getRowBackground(rowIndex)

  const darkBackground = tab.getItemDarkBackgroundColor
    ? tab.getItemDarkBackgroundColor(item, rowIndex)
    : getRowDarkBackground(rowIndex)
  return (
    <Flex
      bg={background}
      _dark={{ bg: darkBackground }}
      px={1}
      flex={width ? undefined : 1}
      overflow="hidden"
      width={`${width ?? 0}px`}
      fontSize="sm"
      align="center"
      onClick={onRowClick ? () => onRowClick(item.id) : undefined}
      key={`${columnIndex}_${rowIndex}`}
      {...flexProps}
      style={{ ...flexProps?.style, ...style }}
      _hover={
        onRowClick
          ? {
              cursor: 'pointer',
              bg: 'gray.100',
              ...flexProps?._hover,
            }
          : flexProps?._hover
      }>
      <Render
        data={item}
        preview={preview}
        tabName={tabName}
        auth={auth}
        admins={admins}
        app={app}
        isScrolling={isScrolling}
        searchQuery={searchQuery}
        cell={{ columnIndex, rowIndex }}
        {...(extraColumnProps as ExtraColumnProps)}
      />
    </Flex>
  )
}

export const outerElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const { closePreview } = useContext(DataGridContext)
  return <div onWheel={closePreview} ref={ref} {...props} />
})

export const DesktopDataList = forwardRef<
  VariableSizeGrid<UserCellComponentProps<any, any>>,
  DataTableProps<any, any, any>
>(
  (
    {
      items,
      itemHeight = 36,
      columns,
      onRowClick,
      tabName,
      tab,
      admins,
      width,
      defaultSort,
      preview: previewView,
      fullWidth,
      extraColumnProps,
      loading,
      height,
    },
    ref,
  ) => {
    const { colorMode } = useColorMode()
    const [displayedMessage, setDisplayedMessage] = useState('')
    const previewState = usePreview()
    const { preview } = previewState
    const { searchQuery } = useContext(SearchBarContext)
    const { arr, map } = useMemo(() => {
      const values = Object.entries(items ?? {}).map(([key, val]) => ({
        ...val,
        id: key,
      }))
      return {
        arr: values as Array<WithId<ListItem>>,
        map: values.reduce<Record<string, WithId<ListItem>>>(
          (acc, curr) => ({
            ...acc,
            [curr.id]: curr,
          }),
          {},
        ),
      }
    }, [items])
    const collection = useMemo(() => tab.baseQuery.collection, [tab])
    const previewItem = useMemo(() => (preview?.id ? map[preview.id] : null), [preview, map])
    const populated = usePopulatedItem(previewItem, collection)
    const reverseName = useMemo(() => getReverseName(populated), [populated])
    const auth = useAuth()
    const app = useApp()

    const flexColWidth = useMemo(() => getFlexColumnWidth({ width, columns }), [columns, width])

    const [remounting, setRemounting] = useState(false)
    useEffect(() => {
      setRemounting(true)
      const timeout = setTimeout(() => {
        setTimeout(() => setRemounting(false), 100)
      }, 500)
      return () => clearTimeout(timeout)
    }, [flexColWidth, tab])

    const itemsWithDividers = useMemo(
      () => getItemsWithDividers(arr, columns, defaultSort),
      [arr, columns, defaultSort],
    )

    const itemData = useMemo<UserCellComponentProps<any, any>>(
      () => ({
        columns,
        auth,
        items: itemsWithDividers,
        app,
        tabName,
        listHeight: height,
        listWidth: width,
        onRowClick,
        tab,
        flexColWidth,
        searchQuery,
        extraColumnProps,
        preview: previewState,
        admins,
      }),
      [
        app,
        auth,
        columns,
        flexColWidth,
        height,
        width,
        previewState,
        searchQuery,
        tab,
        tabName,
        admins,
        onRowClick,
        extraColumnProps,
        itemsWithDividers,
      ],
    )

    // const keys = Object.keys(columns)
    // const numCols = keys.length
    const { keys, numCols } = useMemo(() => {
      const ks = Object.keys(columns)
      return { keys: ks, numCols: ks.length }
    }, [columns])

    const rowHeight = useCallback(
      (idx: number) => {
        if (isListDividerItem(itemsWithDividers[idx])) return 30
        return typeof itemHeight === 'number' ? itemHeight : itemHeight(itemsWithDividers[idx])
      },
      [itemsWithDividers, itemHeight],
    )

    let body: JSX.Element | null = null
    if (remounting || loading) {
      body = (
        <Center width={`${width}px`} height={`${height}px`}>
          <CircularProgress isIndeterminate color="#777" size={10} />
        </Center>
      )
    } else if (itemsWithDividers.length) {
      body = (
        <VariableSizeGrid
          ref={ref}
          outerElementType={outerElementType}
          itemKey={e => `${e.rowIndex}_${e.columnIndex}`}
          style={{
            background: colorMode === 'light' ? 'white' : '#1a1a1a',
            // overflow: 'overlay',
            transition: 'height 500ms',
            overflowX: 'hidden',
          }}
          overscanRowCount={3}
          onScroll={previewState.closePreview}
          itemData={itemData}
          columnCount={numCols}
          useIsScrolling
          rowCount={itemsWithDividers.length}
          rowHeight={rowHeight}
          width={fullWidth}
          height={height}
          columnWidth={(index: number) => columns[keys[index]].width ?? flexColWidth}>
          {UserCell}
        </VariableSizeGrid>
      )
    } else {
      body = (
        <Center width={`${width}px`} height={`${height}px`}>
          <Text color="gray.500" fontSize="lg">
            No {makePlural(tab.itemName.toLowerCase())} found
          </Text>
        </Center>
      )
    }
    return (
      <DataGridContext.Provider
        value={{
          data: map ?? null,
          columns,
          item: populated,
          collection,
          display: setDisplayedMessage,
          width,
          height,
          ...previewState,
          itemHeight,
          // item: populated,
          reverseName,
          clearMessage: () => setDisplayedMessage(''),
        }}>
        <Flex w="100%" pos="relative" direction="column">
          {previewView ? null : (
            <Flex
              bg="white"
              _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
              borderBottom="1px solid"
              borderColor="gray.200"
              w="100%"
              pos="relative">
              <DesktopRowHeaders keys={keys} columns={columns} flexColWidth={flexColWidth} />
              <Flex
                opacity={displayedMessage ? 1 : 0}
                pos="absolute"
                right="10px"
                shadow="md"
                p={2}
                zIndex={3}
                bg="white"
                borderRadius="md">
                {displayedMessage}
              </Flex>
            </Flex>
          )}
          {body}
        </Flex>
      </DataGridContext.Provider>
    )
  },
)
